import {
  Card,
  Button,
  Divider,
  OutlinedInput,
  Grid,
  IconButton,
} from "@mui/material";

import { onChangeProviderNotes } from "app/store/transmission/slice/transmissionSlice";

import React, { useCallback } from "react";
import { useSelector } from "react-redux";

import { Mic as MicIcon } from "@mui/icons-material";

import { USER_ROLES, CLINIC_FOR_REVIEW } from "static/constants";
import { formatLocalDateTime } from "utils/date";
import { getCurrentUserRole } from "utils/currentUser.util";
import { getProviderNoteTitle } from "utils/report";
import NotesIcon from "@mui/icons-material/Notes";
import QuickTextTemplates from "components/pages/transmissions/modal/detail/notes/quick_text_templates/QuickTextTemplates";
import SubmitButton from "components/Button/SubmitButton";
import useProviderNotes from "./useProviderNotes";
import {
  NOTE_SAVE_HANDLERS,
  useRegisterNoteSaveHandler,
} from "../NoteSaveHandlersContext";
import useNoteButtonActionInProgress from "../footer/hooks/useNoteButtonActionInProgress";

const ProviderNotes = ({ data }) => {
  const {
    theme,
    dispatch,
    reportCurrentTab,
    reportDetail,
    providerNotes,
    newProviderNote,
    handleInputChange,
    flushProviderNote,
    cancelProviderNotes,
    onSaveProviderNotes,
    isSpeechRecognizing,
    providerNotesTouched,
    providerNoteSubmitting,
    toggleSpeechRecognition,
    accountsMapper,
  } = useProviderNotes(data);

  const isActionInProgress = useNoteButtonActionInProgress();
  const { isArchiveReport } = useSelector((state) => state.archiveReport);
  const userRole = getCurrentUserRole();

  const providerNotesTitle = getProviderNoteTitle(
    accountsMapper,
    data?.account
  );

  const hasEditAccess =
    !isArchiveReport &&
    !data?.locked &&
    !data?.legacy &&
    userRole === USER_ROLES.PHYSICIAN &&
    reportCurrentTab === CLINIC_FOR_REVIEW;

  useRegisterNoteSaveHandler(
    NOTE_SAVE_HANDLERS.PROVIDER_NOTE_SAVE,
    onSaveProviderNotes
  );

  useRegisterNoteSaveHandler(NOTE_SAVE_HANDLERS.PROVIDER_NOTE_UNSAVED, () => {
    // Flush text still in the debounce
    flushProviderNote();
    return (
      hasEditAccess &&
      (providerNotesTouched ||
        (newProviderNote || "") !== (providerNotes || ""))
    );
  });

  const handleTemplateClick = useCallback(
    (description) => {
      dispatch(
        onChangeProviderNotes({
          note: newProviderNote
            ? `${newProviderNote}\n${description}`
            : description,
          notesTouched: true,
        })
      );
    },
    [dispatch, newProviderNote]
  );

  return (
    <>
      {hasEditAccess && (
        <QuickTextTemplates
          account={data?.account}
          ownTemplatesOnReviewOnly
          onTemplateClick={handleTemplateClick}
          disabled={isActionInProgress}
        />
      )}

      <Card
        style={{
          margin: "12px 0",
          boxShadow: "none",
          border: "1px solid #E0E0E0",
        }}
      >
        <Grid container className="d-flex align-items-center p-2">
          <Grid item xs={1}>
            <NotesIcon className="mr-2" />
          </Grid>
          <Grid item xs={11}>
            <span
              style={{
                fontSize: "14px",
                color: "#444444",
                fontWeight: 500,
              }}
            >
              {providerNotesTitle}
            </span>
          </Grid>
        </Grid>
        <Divider className="mb-2" />

        <div className={!hasEditAccess ? "p-3 pt-0 pb-0" : "p-3"}>
          {!hasEditAccess ? (
            <div className="view-pdf__textarea view-mode mb-0 p-0 border-none">
              {newProviderNote}
            </div>
          ) : (
            <div className="d-flex mt-2">
              <OutlinedInput
                minRows={3}
                maxRows={10}
                multiline
                fullWidth
                variant="outlined"
                id="provider-note"
                data-text="provider-note-field"
                placeholder="Write notes here"
                value={newProviderNote}
                inputProps={{
                  spellCheck: true,
                }}
                onChange={(e) => handleInputChange(e.target.value)}
                style={{
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.background.paper,
                }}
                endAdornment={
                  <IconButton
                    color={isSpeechRecognizing ? "primary" : "default"}
                    onClick={toggleSpeechRecognition}
                    disabled={isActionInProgress}
                    className={isSpeechRecognizing ? "mic-pulse" : undefined}
                    aria-label={
                      isSpeechRecognizing
                        ? "Listening..."
                        : "Start voice dictation"
                    }
                  >
                    <MicIcon />
                  </IconButton>
                }
                disabled={isActionInProgress}
              />
            </div>
          )}

          <div className="process-buttons__wrapper mt-2">
            <div className="d-flex justify-between">
              <div
                className="view-pdf__notes-signature"
                style={{ marginBottom: "8px" }}
              >
                {reportDetail?.provider_note?.updated_by?.name && (
                  <>
                    <span>{reportDetail?.provider_note?.updated_by?.name}</span>{" "}
                    •&nbsp;
                  </>
                )}
                {reportDetail?.provider_note?.updated_at && (
                  <span>
                    {formatLocalDateTime(
                      reportDetail?.provider_note?.updated_at
                    )}
                  </span>
                )}
              </div>
              {hasEditAccess && (
                <div className="d-flex align-items-center flex-end g-1">
                  <Button
                    onClick={cancelProviderNotes}
                    className="view-pdf__button"
                    variant="outlined"
                    color="secondary"
                    disabled={isActionInProgress}
                  >
                    Cancel
                  </Button>

                  <SubmitButton
                    handleClick={() => onSaveProviderNotes()}
                    className="view-pdf__button"
                    variant="contained"
                    color="secondary"
                    isLoading={providerNoteSubmitting}
                    style={{
                      opacity: !providerNotesTouched ? ".3" : "1",
                    }}
                    disabled={!providerNotesTouched || isActionInProgress}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
    </>
  );
};

export default ProviderNotes;
